import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../firebase';
import { compressImageFile } from '../../utils/imageHelpers';
import { guardContentSubmission, showModerationWarning } from '../../utils/moderation';
import { showToast } from '../../utils/toast';
import './CirclePostCreate.css';

const MAX_PHOTOS = 20;
const LEVEL_OPTIONS = [
  { value: '', label: '未設定' },
  { value: 'easy', label: 'ゆるハイク' },
  { value: 'beginner', label: '初級' },
  { value: 'intermediate', label: '中級' },
  { value: 'advanced', label: '健脚・上級' },
  { value: 'snow', label: '雪山' },
];
const WEATHER_OPTIONS = [
  { value: '', label: '未設定' },
  { value: '晴れ', label: '晴れ' },
  { value: '曇り', label: '曇り' },
  { value: '雨', label: '雨' },
  { value: '雪', label: '雪' },
  { value: '晴れ時々曇り', label: '晴れ時々曇り' },
];

const EMPTY_FORM = {
  mountain: '',
  date: '',
  title: '',
  course: '',
  courseTime: '',
  level: '',
  weather: '',
  dangers: '',
  body: '',
  gear: '',
  participants: '',
};

function CirclePostCreate({ editPost }) {
  const { id: circleId, postId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const errorRef = useRef(null);
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [photos, setPhotos] = useState([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState([]);
  const [removedPhotoUrls, setRemovedPhotoUrls] = useState([]);
  const [fieldError, setFieldError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingPost, setLoadingPost] = useState(!!postId);
  const [isDraft, setIsDraft] = useState(false);

  // 編集時に既存データを読み込む
  useEffect(() => {
    if (!postId) return;
    let active = true;
    setLoadingPost(true);

    getDoc(doc(db, 'recruitments', circleId, 'posts', postId))
      .then((snap) => {
        if (!active || !snap.exists()) return;
        const data = snap.data();
        setFormData({
          mountain: data.mountain || '',
          date: data.date || '',
          title: data.title || '',
          course: data.course || '',
          courseTime: data.courseTime || '',
          level: data.level || '',
          weather: data.weather || '',
          dangers: data.dangers || '',
          body: data.body || '',
          gear: data.gear || '',
          participants: data.participants != null ? String(data.participants) : '',
        });
        setExistingPhotoUrls(Array.isArray(data.photoUrls) ? data.photoUrls : []);
        setIsDraft(data.status === 'draft');
      })
      .catch(() => showToast({ message: '投稿データの取得に失敗しました。', type: 'error' }))
      .finally(() => { if (active) setLoadingPost(false); });

    return () => { active = false; };
  }, [circleId, postId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoAdd = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    const total = existingPhotoUrls.length + photos.length + files.length;
    if (total > MAX_PHOTOS) {
      setFieldError(`写真は最大${MAX_PHOTOS}枚までです。`);
      return;
    }
    const oversized = files.filter((f) => f.size > 10 * 1024 * 1024);
    if (oversized.length) {
      setFieldError('10MB を超える画像は選択できません。');
      return;
    }
    setFieldError('');
    try {
      const compressed = await Promise.all(
        files.map((f) => compressImageFile(f, { maxWidth: 1920, maxHeight: 1920, quality: 0.82, outputType: 'image/jpeg' }))
      );
      setPhotos((prev) => [...prev, ...compressed.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    } catch {
      setFieldError('画像の圧縮に失敗しました。別の画像でお試しください。');
    }
    e.target.value = '';
  }, [existingPhotoUrls.length, photos.length]);

  const removeNewPhoto = (index) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingPhoto = (url) => {
    setExistingPhotoUrls((prev) => prev.filter((u) => u !== url));
    setRemovedPhotoUrls((prev) => [...prev, url]);
  };

  const scrollToError = () => {
    window.setTimeout(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const validate = () => {
    if (!formData.mountain.trim()) return '山名を入力してください。';
    if (!formData.date) return '山行日を選択してください。';
    if (!formData.body.trim()) return '感想・本文を入力してください。';
    if (formData.mountain.length > 100) return '山名は100文字以内で入力してください。';
    if (formData.body.length > 5000) return '感想・本文は5000文字以内で入力してください。';
    return '';
  };

  const uploadNewPhotos = async () => {
    const urls = [];
    for (const { file } of photos) {
      const storageRef = ref(storage, `recruitment_images/${circleId}/posts/${Date.now()}_${Math.random().toString(36).slice(2)}`);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  };

  const handleSave = async (status) => {
    if (!currentUser) { navigate('/login'); return; }
    const validationError = validate();
    if (validationError) {
      setFieldError(validationError);
      scrollToError();
      return;
    }

    setSubmitting(true);
    setFieldError('');

    try {
      const moderation = await guardContentSubmission({
        action: 'post_create',
        contentType: 'circle_post',
        texts: [formData.mountain, formData.title, formData.body, formData.dangers, formData.gear],
        metadata: { circleId },
      });
      showModerationWarning(moderation);

      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const userProfile = userSnap.exists() ? userSnap.data() : {};

      const newPhotoUrls = await uploadNewPhotos();
      const photoUrls = [...existingPhotoUrls, ...newPhotoUrls];

      const postData = {
        mountain: formData.mountain.trim(),
        date: formData.date,
        title: formData.title.trim(),
        course: formData.course.trim(),
        courseTime: formData.courseTime.trim(),
        level: formData.level,
        weather: formData.weather,
        dangers: formData.dangers.trim(),
        body: formData.body.trim(),
        gear: formData.gear.trim(),
        participants: formData.participants ? Number(formData.participants) : null,
        photoUrls,
        authorId: currentUser.uid,
        authorNickname: userProfile.nickname || currentUser.displayName || '登山ユーザー',
        authorPhotoURL: userProfile.photoURL || currentUser.photoURL || '',
        status,
        updatedAt: serverTimestamp(),
        ...(status === 'published' && !postId ? { publishedAt: serverTimestamp() } : {}),
        ...(status === 'published' && postId && isDraft ? { publishedAt: serverTimestamp() } : {}),
      };

      if (postId) {
        await updateDoc(doc(db, 'recruitments', circleId, 'posts', postId), postData);
        showToast({
          message: status === 'published' ? '投稿を更新しました。' : '下書きを保存しました。',
          type: 'success',
        });
        navigate(status === 'published' ? `/circles/${circleId}/posts/${postId}` : `/circles/${circleId}/manage`);
      } else {
        const docRef = await addDoc(collection(db, 'recruitments', circleId, 'posts'), {
          ...postData,
          createdAt: serverTimestamp(),
          likes: 0,
          commentsCount: 0,
        });
        showToast({
          message: status === 'published' ? '活動記録を公開しました。' : '下書きを保存しました。',
          type: 'success',
        });
        navigate(status === 'published' ? `/circles/${circleId}/posts/${docRef.id}` : `/circles/${circleId}/manage`);
      }
    } catch (error) {
      console.error('CirclePost save failed:', error);
      setFieldError('保存に失敗しました。時間をおいて再度お試しください。');
      scrollToError();
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPost) return <div className="loading">読み込み中...</div>;

  const totalPhotos = existingPhotoUrls.length + photos.length;

  return (
    <div className="cpc-page">
      <div className="cpc-header">
        <h1>{postId ? '活動記録を編集' : '活動記録を投稿'}</h1>
        <p>サークルの山行記録を残しましょう。写真も最大{MAX_PHOTOS}枚まで追加できます。</p>
      </div>

      {fieldError && (
        <div ref={errorRef} className="cpc-error" role="alert" aria-live="assertive" tabIndex="-1">
          {fieldError}
        </div>
      )}

      <form className="cpc-form" noValidate onSubmit={(e) => e.preventDefault()}>
        <section className="cpc-section">
          <div className="cpc-section__head"><span>01</span><h2>山行情報</h2></div>
          <div className="cpc-grid">
            <label className="cpc-field cpc-field--required">
              <span>山名</span>
              <input name="mountain" value={formData.mountain} onChange={handleChange} placeholder="例: 高尾山" maxLength={100} required />
            </label>
            <label className="cpc-field cpc-field--required">
              <span>山行日</span>
              <input type="date" name="date" value={formData.date} onChange={handleChange} required />
            </label>
            <label className="cpc-field">
              <span>タイトル</span>
              <input name="title" value={formData.title} onChange={handleChange} placeholder="例: 紅葉が美しかった秋の高尾山" maxLength={120} />
            </label>
            <label className="cpc-field">
              <span>コース</span>
              <input name="course" value={formData.course} onChange={handleChange} placeholder="例: 1号路 → 山頂 → 稲荷山コース" maxLength={200} />
            </label>
            <label className="cpc-field">
              <span>コースタイム</span>
              <input name="courseTime" value={formData.courseTime} onChange={handleChange} placeholder="例: 登り1:30 / 下り1:10" maxLength={80} />
            </label>
            <label className="cpc-field">
              <span>レベル</span>
              <select name="level" value={formData.level} onChange={handleChange}>
                {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="cpc-field">
              <span>天気</span>
              <select name="weather" value={formData.weather} onChange={handleChange}>
                {WEATHER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="cpc-field">
              <span>参加人数</span>
              <input type="number" name="participants" value={formData.participants} onChange={handleChange} min="1" max="999" placeholder="例: 8" />
            </label>
          </div>
        </section>

        <section className="cpc-section">
          <div className="cpc-section__head"><span>02</span><h2>内容</h2></div>
          <label className="cpc-field cpc-field--full cpc-field--required">
            <span>感想・本文</span>
            <textarea
              name="body"
              value={formData.body}
              onChange={handleChange}
              rows={10}
              maxLength={5000}
              placeholder="山行の様子、雰囲気、感じたことを書いてください。初参加の方の感想なども歓迎です。"
              required
            />
            <small>{formData.body.length} / 5000文字</small>
          </label>
          <label className="cpc-field cpc-field--full">
            <span>危険箇所・注意点</span>
            <textarea
              name="dangers"
              value={formData.dangers}
              onChange={handleChange}
              rows={4}
              maxLength={800}
              placeholder="例: 山頂付近は岩場あり。雨天後は滑りやすいので注意。"
            />
          </label>
          <label className="cpc-field cpc-field--full">
            <span>持ち物・装備メモ</span>
            <textarea
              name="gear"
              value={formData.gear}
              onChange={handleChange}
              rows={4}
              maxLength={800}
              placeholder="例: 登山靴、レインウェア、水1.5L、行動食。"
            />
          </label>
        </section>

        <section className="cpc-section">
          <div className="cpc-section__head"><span>03</span><h2>写真</h2></div>
          <p className="cpc-photo-note">最大{MAX_PHOTOS}枚まで。1枚10MB以下のJPG・PNG・HEIC。</p>

          {(existingPhotoUrls.length > 0 || photos.length > 0) && (
            <div className="cpc-photo-grid">
              {existingPhotoUrls.map((url) => (
                <div key={url} className="cpc-photo-item">
                  <img src={url} alt="投稿写真" />
                  <button type="button" className="cpc-photo-remove" onClick={() => removeExistingPhoto(url)} aria-label="写真を削除">×</button>
                </div>
              ))}
              {photos.map((p, i) => (
                <div key={p.preview} className="cpc-photo-item">
                  <img src={p.preview} alt={`新規写真${i + 1}`} />
                  <button type="button" className="cpc-photo-remove" onClick={() => removeNewPhoto(i)} aria-label="写真を削除">×</button>
                </div>
              ))}
            </div>
          )}

          {totalPhotos < MAX_PHOTOS && (
            <>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoAdd}
                className="cpc-photo-input"
                aria-label="写真を追加"
              />
              <button type="button" className="cpc-photo-add-btn" onClick={() => photoInputRef.current?.click()}>
                写真を追加（{totalPhotos}/{MAX_PHOTOS}）
              </button>
            </>
          )}
        </section>

        <div className="cpc-actions">
          <button
            type="button"
            className="cpc-btn cpc-btn--ghost"
            onClick={() => handleSave('draft')}
            disabled={submitting}
          >
            {submitting ? '保存中...' : '下書き保存'}
          </button>
          <button
            type="button"
            className="cpc-btn cpc-btn--primary"
            onClick={() => handleSave('published')}
            disabled={submitting}
          >
            {submitting ? '公開中...' : postId && !isDraft ? '更新する' : '公開する'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CirclePostCreate;
